import { useState, type ButtonHTMLAttributes, type CSSProperties } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
}

const VARIANT_STYLES: Record<ButtonVariant, CSSProperties> = {
  primary: {
    backgroundColor: 'var(--color-primary-600)',
    color: 'var(--text-inverse)',
  },
  secondary: {
    backgroundColor: 'var(--bg-button-secondary)',
    color: 'var(--text-primary)',
  },
  danger: {
    backgroundColor: 'var(--color-error-600)',
    color: 'var(--text-inverse)',
  },
  ghost: {
    backgroundColor: 'transparent',
    color: 'var(--text-secondary)',
  },
};

const VARIANT_HOVER: Record<ButtonVariant, CSSProperties> = {
  primary: { backgroundColor: 'var(--color-primary-500)' },
  secondary: { backgroundColor: 'var(--bg-button-secondary-hover)' },
  danger: { backgroundColor: 'var(--color-error-500)' },
  ghost: { backgroundColor: 'var(--bg-surface-sunken)' },
};

const SIZE_STYLES: Record<ButtonSize, CSSProperties> = {
  sm: { padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-sm)', minHeight: '32px' },
  md: { padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-sm)', minHeight: '40px' },
  lg: { padding: 'var(--space-3) var(--space-6)', fontSize: 'var(--text-base)', minHeight: '48px' },
};

export const Button = ({
  variant = 'primary',
  size = 'md',
  className = '',
  style,
  children,
  disabled,
  ...props
}: ButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const variantStyle = !disabled && isHovered
    ? { ...VARIANT_STYLES[variant], ...VARIANT_HOVER[variant] }
    : VARIANT_STYLES[variant];

  return (
    <button
      className={`focus-ring inline-flex items-center justify-center ${className}`}
      style={{
        borderRadius: 'var(--radius-md)',
        fontWeight: 'var(--font-weight-medium)',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: `background-color var(--duration-fast) var(--ease-out)`,
        ...variantStyle,
        ...SIZE_STYLES[size],
        ...style,
      }}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {children}
    </button>
  );
};
